/**
 * DEV-ONLY: Classic layout inspector panel UI. Imported via ClassicInspectorPanel.jsx.
 */
import React from "react";
import { COLOR_OVERRIDE_KEYS, normalizeCssColorToHex6 } from "./layoutInspectorColorFields";

const TEMP_EDIT_PROPS = [
  { key: "padding", label: "Padding", unit: "px" },
  { key: "margin", label: "Margin", unit: "px" },
  { key: "width", label: "Width", unit: "px" },
  { key: "maxWidth", label: "Max width", unit: "px" },
  { key: "fontSize", label: "Font size", unit: "px" },
  { key: "lineHeight", label: "Line height", unit: "" },
  { key: "gap", label: "Gap", unit: "px" },
  { key: "borderRadius", label: "Border radius", unit: "px" },
];

function Row({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="layout-inspector-panel__row">
      <span className="layout-inspector-panel__label">{label}</span>
      <span className="layout-inspector-panel__value" title={String(value)}>
        {String(value)}
      </span>
    </div>
  );
}

function hasAnyTempOverrides(overrides) {
  return Object.keys(overrides).some((k) => {
    if (k === "mediaComposition") {
      const mc = overrides.mediaComposition;
      return mc && typeof mc === "object" && Object.keys(mc).length > 0;
    }
    if (k === "reviewCardHeightPx") {
      return overrides.reviewCardHeightPx !== undefined && overrides.reviewCardHeightPx !== "";
    }
    if (COLOR_OVERRIDE_KEYS.includes(k)) {
      return overrides[k] !== undefined && overrides[k] !== "";
    }
    return overrides[k] !== undefined && overrides[k] !== "";
  });
}

export default function LayoutInspectorPanel({
  layoutInfo,
  showHoverOverlay,
  onToggleHoverOverlay,
  onClearSelection,
  onClose,
  overrides = {},
  onOverridesChange,
  onResetSelectedEdits,
  mediaCompositionInfo = null,
  onMediaCompositionPatch,
  reviewCardHeightInfo = null,
  onReviewCardHeightPatch,
}) {
  const editableComputed = layoutInfo?.editableComputed ?? {};
  const hasOverrides = hasAnyTempOverrides(overrides);

  return (
    <div className="layout-inspector-panel" aria-label="Layout inspector controls">
      {layoutInfo ? (
        <div className="layout-inspector-panel__section">
          <Row label="Tag" value={layoutInfo.tagName} />
          <Row label="Class" value={layoutInfo.className || "(none)"} />
          <Row label="Size" value={`${layoutInfo.width} × ${layoutInfo.height}`} />
          <Row label="Margin" value={layoutInfo.margin} />
          <Row label="Padding" value={layoutInfo.padding} />
          <Row label="Border radius" value={layoutInfo.borderRadius} />
          <Row label="Font size" value={layoutInfo.fontSize} />
          <Row label="Line height" value={layoutInfo.lineHeight} />
          <Row label="Display" value={layoutInfo.display} />
          <Row label="Position" value={layoutInfo.position} />
          <Row label="Gap" value={layoutInfo.gap} />
          {(layoutInfo.flexDirection || layoutInfo.justifyContent || layoutInfo.alignItems) && (
            <>
              <Row label="Flex dir" value={layoutInfo.flexDirection} />
              <Row label="Justify" value={layoutInfo.justifyContent} />
              <Row label="Align" value={layoutInfo.alignItems} />
            </>
          )}
        </div>
      ) : (
        <p className="layout-inspector-panel__hint">Click an element to select it.</p>
      )}

      {layoutInfo && layoutInfo.colorFields?.length > 0 && onOverridesChange && (
        <div className="layout-inspector-panel__section layout-inspector-panel__tune layout-inspector-panel__color-section">
          <div className="layout-inspector-panel__tune-title">Color</div>
          <p className="layout-inspector-panel__tune-hint">
            Inline color overrides (same temp session as TEMP EDITS). Use the text field for rgba, named colors, or values the picker cannot represent.
          </p>
          {layoutInfo.colorFields.map((field) => {
            const computed = field.computedValue ?? "";
            const overrideVal = overrides[field.key];
            const displayValue =
              overrideVal !== undefined && overrideVal !== "" ? String(overrideVal) : computed;
            const isOverride = overrideVal !== undefined && overrideVal !== "";
            const hexForPicker = normalizeCssColorToHex6(displayValue);
            const computedShort = computed.length > 18 ? `${computed.slice(0, 15)}…` : computed;
            return (
              <div
                key={field.key}
                className={`layout-inspector-panel__color-block ${isOverride ? "layout-inspector-panel__color-block--edited" : ""}`}
              >
                <div className="layout-inspector-panel__color-head">
                  <span className="layout-inspector-panel__label">{field.label}</span>
                  <span className="layout-inspector-panel__tune-original" title={`Computed: ${computed || "—"}`}>
                    {computed ? computedShort : "—"}
                  </span>
                </div>
                <div className="layout-inspector-panel__color-tools">
                  <span
                    className="layout-inspector-panel__color-swatch"
                    style={{ background: displayValue || "transparent" }}
                    title={displayValue || "—"}
                    aria-hidden
                  />
                  {hexForPicker ? (
                    <input
                      type="color"
                      className="layout-inspector-panel__color-input-native"
                      value={hexForPicker}
                      onChange={(e) => {
                        const v = e.target.value;
                        onOverridesChange((prev) => ({ ...prev, [field.key]: v }));
                      }}
                      aria-label={`${field.label} native picker`}
                    />
                  ) : (
                    <span className="layout-inspector-panel__color-input-fallback" title="No hex equivalent — edit as text">
                      —
                    </span>
                  )}
                  <input
                    type="text"
                    className="layout-inspector-panel__tune-input layout-inspector-panel__tune-input--color-value"
                    spellCheck={false}
                    value={displayValue}
                    onChange={(e) => {
                      const v = e.target.value.trim();
                      onOverridesChange((prev) =>
                        v === "" ? { ...prev, [field.key]: undefined } : { ...prev, [field.key]: v }
                      );
                    }}
                    aria-label={`${field.label} CSS value`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {layoutInfo && mediaCompositionInfo && onMediaCompositionPatch && (
        <div className="layout-inspector-panel__section layout-inspector-panel__media-comp">
          <div className="layout-inspector-panel__tune-title">Media composition</div>
          <p className="layout-inspector-panel__media-comp-target">
            Target: <strong>{mediaCompositionInfo.meta.label}</strong>
          </p>
          <p className="layout-inspector-panel__tune-hint">
            Object position (X/Y %) and scale. Values apply as temp overrides (see session persistence).
          </p>
          <div className="layout-inspector-panel__media-comp-grid">
            <label className="layout-inspector-panel__media-comp-field">
              <span className="layout-inspector-panel__label">Pos X (%)</span>
              <input
                type="number"
                className="layout-inspector-panel__tune-input layout-inspector-panel__tune-input--wide"
                value={Number.isFinite(mediaCompositionInfo.merged.positionX) ? mediaCompositionInfo.merged.positionX : ""}
                onChange={(e) => {
                  const n = parseFloat(e.target.value);
                  onMediaCompositionPatch({ positionX: Number.isFinite(n) ? n : 50 });
                }}
                step={1}
                aria-label="Object position X percent"
              />
            </label>
            <label className="layout-inspector-panel__media-comp-field">
              <span className="layout-inspector-panel__label">Pos Y (%)</span>
              <input
                type="number"
                className="layout-inspector-panel__tune-input layout-inspector-panel__tune-input--wide"
                value={Number.isFinite(mediaCompositionInfo.merged.positionY) ? mediaCompositionInfo.merged.positionY : ""}
                onChange={(e) => {
                  const n = parseFloat(e.target.value);
                  onMediaCompositionPatch({ positionY: Number.isFinite(n) ? n : 50 });
                }}
                step={1}
                aria-label="Object position Y percent"
              />
            </label>
            <label className="layout-inspector-panel__media-comp-field layout-inspector-panel__media-comp-field--full">
              <span className="layout-inspector-panel__label">Scale</span>
              <input
                type="number"
                className="layout-inspector-panel__tune-input layout-inspector-panel__tune-input--wide"
                value={Number.isFinite(mediaCompositionInfo.merged.scale) ? mediaCompositionInfo.merged.scale : ""}
                onChange={(e) => {
                  const n = parseFloat(e.target.value);
                  onMediaCompositionPatch({ scale: Number.isFinite(n) ? n : 1 });
                }}
                step={0.05}
                min={0.05}
                aria-label="Image scale"
              />
            </label>
          </div>
        </div>
      )}

      {layoutInfo && reviewCardHeightInfo && onReviewCardHeightPatch && (
        <div className="layout-inspector-panel__section layout-inspector-panel__media-comp">
          <div className="layout-inspector-panel__tune-title">Card box</div>
          <p className="layout-inspector-panel__media-comp-target">
            Target: <strong>{reviewCardHeightInfo.meta.label}</strong>
          </p>
          <p className="layout-inspector-panel__tune-hint">
            Card height writes a CSS length to <code>{reviewCardHeightInfo.meta.varName}</code> on the card root.
          </p>
          <div className="layout-inspector-panel__media-comp-grid">
            <label className="layout-inspector-panel__media-comp-field layout-inspector-panel__media-comp-field--full">
              <span className="layout-inspector-panel__label">Card height (px)</span>
              <input
                type="number"
                className="layout-inspector-panel__tune-input layout-inspector-panel__tune-input--wide"
                value={Number.isFinite(reviewCardHeightInfo.merged.heightPx) ? reviewCardHeightInfo.merged.heightPx : ""}
                onChange={(e) => {
                  const n = parseFloat(e.target.value);
                  onReviewCardHeightPatch({ heightPx: Number.isFinite(n) ? n : 360 });
                }}
                step={1}
                min={120}
                aria-label="Review card height in pixels"
              />
            </label>
          </div>
        </div>
      )}

      {layoutInfo && onOverridesChange && (
        <div className="layout-inspector-panel__section layout-inspector-panel__tune">
          <div className="layout-inspector-panel__tune-title">TEMP EDITS</div>
          <p className="layout-inspector-panel__tune-hint">Temporary, not saved. Copy values to source.</p>
          {TEMP_EDIT_PROPS.map(({ key, label, unit }) => {
            const computed = editableComputed[key];
            const value = overrides[key] !== undefined && overrides[key] !== "" ? overrides[key] : computed;
            const displayValue = value !== undefined && value !== "" ? value : "";
            const isOverride = overrides[key] !== undefined && overrides[key] !== "";
            const pxNum = key === "fontSize" && displayValue !== "" ? parseFloat(displayValue, 10) : NaN;
            const remValue = !Number.isNaN(pxNum) && pxNum > 0 ? (pxNum / 16).toFixed(3).replace(/\.?0+$/, "") : null;
            return (
              <div key={key} className={`layout-inspector-panel__tune-row ${isOverride ? "layout-inspector-panel__tune-row--edited" : ""}`}>
                <span className="layout-inspector-panel__label">{label}</span>
                <span className="layout-inspector-panel__tune-original" title="Computed">
                  {computed !== undefined && computed !== "" ? `${computed}${unit}` : "—"}
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="layout-inspector-panel__tune-input"
                  placeholder={computed !== undefined && computed !== "" ? String(computed) : "—"}
                  value={displayValue}
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    onOverridesChange((prev) => (v === "" ? { ...prev, [key]: undefined } : { ...prev, [key]: v }));
                  }}
                  aria-label={`${label} (px)`}
                />
                {unit && <span className="layout-inspector-panel__tune-unit">{unit}</span>}
                {key === "fontSize" && remValue !== null && (
                  <span className="layout-inspector-panel__tune-rem" title="Equivalent in rem">{remValue} rem</span>
                )}
              </div>
            );
          })}
          {hasOverrides && onResetSelectedEdits && (
            <button type="button" className="layout-inspector-panel__btn layout-inspector-panel__btn--secondary" onClick={onResetSelectedEdits}>
              Reset selected element edits
            </button>
          )}
        </div>
      )}

      <div className="layout-inspector-panel__actions">
        <label className="layout-inspector-panel__checkbox">
          <input
            type="checkbox"
            checked={showHoverOverlay}
            onChange={(e) => onToggleHoverOverlay(e.target.checked)}
          />
          <span>Hover highlight</span>
        </label>
        <button type="button" className="layout-inspector-panel__btn" onClick={onClearSelection}>
          Clear selection
        </button>
        <button type="button" className="layout-inspector-panel__btn layout-inspector-panel__btn--primary" onClick={onClose}>
          Close inspector
        </button>
      </div>
    </div>
  );
}
